/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Value-provenance chip — R4/R4b/R4d.
 *
 * Renders what the system decided about a value. **Never rendered for an
 * operator-authored value:** there is deliberately no `user` member, because an
 * unchipped row is plainly user-set and chipping everything makes the chips
 * invisible — the failure a provenance system dies of.
 *
 * Deliberately quieter than a status badge. The design system states the reason:
 * these sit on every row of a list rather than once in a header, so they are
 * smaller, lower-contrast, and lose to the value beside them.
 */

import React from 'react';
import { Box } from '@chakra-ui/react';
import type { ValueProvenance } from './parameter-editor.adapters';
import { PROVENANCE_LABEL, PROVENANCE_TONE } from './provenance-presentation';

const TONE_STYLE = {
  info: { bg: 'info.tint', color: 'info.ink', borderColor: 'info.border' },
  neutral: { bg: 'bg.subtle', color: 'fg.muted', borderColor: 'border.subtle' },
} as const;

export interface ProvenanceChipProps {
  provenance?: ValueProvenance;
}

export const ProvenanceChip: React.FC<ProvenanceChipProps> = ({ provenance }) => {
  if (!provenance) return null;

  const tone = TONE_STYLE[PROVENANCE_TONE[provenance]];

  return (
    <Box
      as="span"
      flexShrink={0}
      fontSize="10px"
      lineHeight="1.4"
      fontFamily="mono"
      borderWidth="1px"
      borderRadius="full"
      px={1.5}
      py={0}
      whiteSpace="nowrap"
      {...tone}
    >
      {PROVENANCE_LABEL[provenance]}
    </Box>
  );
};
